import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, CheckCircle2, Tag, ChevronDown, ChevronUp, Sparkles, ShieldCheck } from 'lucide-react';
import storage from '../../services/storageService';

const LoginScreen = ({ onGoogleLogin, error: externalError, loading: parentLoading }) => {
    const [authStatus, setAuthStatus] = useState('idle'); // 'idle' | 'connecting' | 'success' | 'error'
    const [statusMessage, setStatusMessage] = useState('');
    const [localError, setLocalError] = useState(null);

    const [couponCode, setCouponCode] = useState(() => storage.getItemSync('pending_coupon', ''));
    const [showCouponInput, setShowCouponInput] = useState(() => !!storage.getItemSync('pending_coupon', ''));
    const [autoCouponActive, setAutoCouponActive] = useState(() => !!storage.getItemSync('pending_coupon', ''));

    // Sincroniza se o cupom mudar no storage (ex: capturado pelo link de indicação)
    useEffect(() => {
        const checkCoupon = () => {
            const pending = storage.getItemSync('pending_coupon', '');
            if (pending && !couponCode) {
                setCouponCode(pending);
                setShowCouponInput(true);
                setAutoCouponActive(true);
            }
        };
        const interval = setInterval(checkCoupon, 1000);
        return () => clearInterval(interval);
    }, [couponCode]);

    const handleGoogleLogin = async () => {
        if (authStatus !== 'idle') return; // Previne cliques múltiplos concorrentes

        setLocalError(null);
        setAuthStatus('connecting');
        setStatusMessage('Conectando com o Google...');

        try {
            const resultUser = await onGoogleLogin(couponCode, (stepText) => {
                setStatusMessage(stepText);
            });

            if (resultUser) {
                // Sucesso confirmado! Mantém travado em estado de sucesso para a troca de tela
                setAuthStatus('success');
                setStatusMessage('Login realizado com sucesso! Entrando...');
            } else {
                // Usuário cancelou ou fechou a janela do Google sem escolher conta
                setAuthStatus('idle');
                setStatusMessage('');
            }
        } catch (err) {
            console.error('[LoginScreen] Falha na autenticação Google:', err);
            setAuthStatus('error');
            setLocalError(err?.message || 'Falha ao autenticar com o Google. Tente novamente.');
        }
    };

    const isProcessing = authStatus === 'connecting' || authStatus === 'success' || parentLoading;
    const displayError = localError || externalError;

    return (
        <div className="fixed inset-0 overflow-y-auto flex flex-col p-4 bg-transparent">
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 animate-fade-in m-auto my-8 border border-gray-100 dark:border-gray-800">
                {/* Logo / Header */}
                <div className="text-center mb-6">
                    <img
                        src="/icon-512.png"
                        alt="MeuPainel"
                        className="w-16 h-16 mx-auto mb-3 rounded-2xl shadow-lg shadow-indigo-100 dark:shadow-none"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl mx-auto mb-3 items-center justify-center shadow-lg hidden">
                        <span className="text-2xl text-white font-bold">MP</span>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">MeuPainel</h1>
                    <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                        Controle financeiro inteligente para motoristas
                    </p>
                </div>

                {/* Banner de Cupom Ativo */}
                {autoCouponActive && couponCode && (
                    <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-3 mb-5 flex items-center gap-3 animate-in slide-in-from-top-2 duration-300">
                        <div className="bg-emerald-500 text-white p-2 rounded-xl shadow-sm">
                            <Sparkles size={16} />
                        </div>
                        <div className="flex-1">
                            <p className="text-[11px] font-black text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                                Indicação Ativada!
                            </p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-500 font-medium">
                                O código <span className="font-bold underline">{couponCode}</span> será aplicado à sua conta.
                            </p>
                        </div>
                    </div>
                )}

                {/* Área de Cupom / Código de Parceiro */}
                <div className="mb-5">
                    <button
                        type="button"
                        onClick={() => setShowCouponInput(!showCouponInput)}
                        disabled={isProcessing}
                        className="flex items-center justify-center gap-2 text-xs text-indigo-600 dark:text-indigo-400 font-semibold hover:underline mx-auto py-1 disabled:opacity-50"
                    >
                        <Tag size={14} />
                        {showCouponInput ? 'Ocultar código de indicação' : 'Tem um código de indicação ou parceiro?'}
                        {showCouponInput ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {showCouponInput && (
                        <div className="animate-fade-in mt-2 mb-2">
                            <div className="relative">
                                <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    className="w-full pl-9 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white uppercase text-xs placeholder:normal-case font-medium"
                                    placeholder="Digite o código do parceiro"
                                    disabled={isProcessing}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Exibição de Erro Amigável */}
                {displayError && (
                    <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl p-3 mb-5 flex items-start gap-2.5 text-red-700 dark:text-red-300 animate-in fade-in">
                        <AlertCircle size={18} className="shrink-0 mt-0.5 text-red-500" />
                        <div className="flex-1 text-xs font-medium">
                            {displayError}
                        </div>
                    </div>
                )}

                {/* Card de Progresso / Carregando */}
                {authStatus === 'connecting' && (
                    <div className="bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800/60 rounded-2xl p-4 mb-5 flex items-center gap-3 animate-in fade-in">
                        <Loader2 size={22} className="animate-spin text-indigo-600 dark:text-indigo-400 shrink-0" />
                        <div className="flex-1">
                            <p className="text-xs font-bold text-indigo-900 dark:text-indigo-200">
                                {statusMessage || 'Conectando ao Google...'}
                            </p>
                            <p className="text-[11px] text-indigo-600/80 dark:text-indigo-300/80 mt-0.5">
                                Aguarde um momento enquanto validamos seu acesso.
                            </p>
                        </div>
                    </div>
                )}

                {/* Card de Sucesso */}
                {authStatus === 'success' && (
                    <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-4 mb-5 flex items-center gap-3 animate-in fade-in">
                        <CheckCircle2 size={24} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <div className="flex-1">
                            <p className="text-xs font-bold text-emerald-900 dark:text-emerald-200">
                                Autenticado com sucesso!
                            </p>
                            <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                                Carregando seus dados no MeuPainel...
                            </p>
                        </div>
                    </div>
                )}

                {/* Botão Google Login (ÚNICO MÉTODO OFICIAL) */}
                <button
                    onClick={handleGoogleLogin}
                    disabled={isProcessing}
                    className={`w-full py-4 px-4 font-bold rounded-2xl transition-all duration-200 shadow-lg flex items-center justify-center gap-3 select-none ${
                        authStatus === 'success'
                            ? 'bg-emerald-600 text-white shadow-emerald-200 cursor-default'
                            : isProcessing
                            ? 'bg-indigo-700 text-white shadow-none opacity-90 cursor-wait'
                            : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white shadow-indigo-200 dark:shadow-none'
                    }`}
                >
                    {isProcessing ? (
                        <>
                            <Loader2 size={20} className="animate-spin text-white" />
                            <span className="text-sm font-semibold tracking-wide">
                                {statusMessage || 'Autenticando...'}
                            </span>
                        </>
                    ) : (
                        <>
                            <div className="bg-white p-1.5 rounded-full flex items-center justify-center shadow-sm">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
                                    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.462.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
                                </svg>
                            </div>
                            <span className="text-sm font-bold">Continuar com Google</span>
                        </>
                    )}
                </button>

                {/* Informações de Segurança e Praticidade */}
                <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs">
                        <ShieldCheck size={15} className="text-indigo-500 shrink-0" />
                        <span>Login rápido e seguro em 1 clique</span>
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                        Seus dados são salvos e sincronizados com sua Conta Google.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginScreen;
